import { createTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";

// Define keyframe animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const zoomIn = keyframes`
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

// Custom theme
const theme = createTheme({
  palette: {
    primary: {
      main: "#2a6a8f", // Spiritual blue - calm, trust, peace
      contrastText: "#ffffff", // White text for contrast
    },
    secondary: {
      main: "#ffd700", // Gold - symbolizes hope and faith
      contrastText: "#ffffff",
    },
    background: {
      default: "#f1f0f4", // Light, neutral background for serenity
      paper: "#ffffff", // White paper for cards and surfaces
    },
    text: {
      primary: "#333333", // Dark, readable text
      secondary: "#555555", // Slightly muted text for secondary content
    },
    success: {
      main: "#4caf50", // Green for success messages
    },
    error: {
      main: "#f44336", // Red for error messages
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Merriweather', 'sans-serif'",
    h1: {
      fontSize: "3rem",
      fontWeight: 700,
      lineHeight: 1.2,
      color: "#2a6a8f", // Matches primary color
      "@media (max-width:600px)": {
        fontSize: "2.5rem",
      },
    },
    h2: {
      fontSize: "2.4rem",
      fontWeight: 600,
      color: "#2a6a8f",
      "@media (max-width:600px)": {
        fontSize: "2rem",
      },
    },
    h6: {
      fontSize: "1.25rem",
      fontWeight: 600,
      color: "#2a6a8f",
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
      color: "#333333",
    },
    body2: {
      fontSize: "0.9rem",
      lineHeight: 1.6,
      color: "#555555",
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },
  spacing: 8,
  keyframes: { fadeIn, fadeInUp, slideIn, pulse, zoomIn },
  transitions: {
    duration: {
      enteringScreen: 400,
      leavingScreen: 300,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#2a6a8f",
          color: "#ffffff",
          transition: "background-color 0.3s ease",
          animation: "fadeIn 1.5s ease-in-out",
          "&:hover": {
            backgroundColor: "#1d4f70", // Darker blue on hover
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          transition: "all 0.3s ease",
          animation: "bounce 1s ease infinite",
          fontWeight: 600,
          "&:hover": {
            backgroundColor: "#ffd700", // Gold hover effect
            color: "#ffffff",
            transform: "scale(1.1)",
            boxShadow: "0px 4px 15px rgba(255, 193, 7, 0.3)",
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          transition: "color 0.3s ease",
          animation: "slideIn 1s ease-out",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          animation: "zoomIn 1s ease-in-out",
          backgroundSize: "cover",
          backgroundPosition: "center",
          "&:hover": {
            transform: "scale(1.02)",
            boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.2)",
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: "1200px",
          animation: "fadeInUp 1s ease",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          color: "#2a6a8f", // Link color in spiritual blue
          transition: "color 0.3s ease",
          "&:hover": {
            color: "#ffd700", // Gold color on hover
            textDecoration: "underline",
            animation: "pulse 1s infinite",
          },
        },
      },
    },
  },
});

// Adding Keyframe Animations
keyframes.keyframes = {
  fadeIn: {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  fadeInUp: {
    from: { opacity: 0, transform: "translateY(20px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  bounce: {
    "0%, 20%, 50%, 80%, 100%": { transform: "translateY(0)" },
    "40%": { transform: "translateY(-10px)" },
    "60%": { transform: "translateY(-5px)" },
  },
  slideIn: {
    from: { transform: "translateX(-100%)", opacity: 0 },
    to: { transform: "translateX(0)", opacity: 1 },
  },
  zoomIn: {
    from: { transform: "scale(0.8)", opacity: 0 },
    to: { transform: "scale(1)", opacity: 1 },
  },
  pulse: {
    "0%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.05)" },
    "100%": { transform: "scale(1)" },
  },
};

export default theme;
